'use client';
import { prepareCaptchaBody } from '@eliadmin/core/src/api/recaptcha-client';
import { CaptchaAction } from '@eliadmin/core/src/api/recaptcha-actions';

import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { createInitialEditorState } from '../domain/design';
import type { ProductConfig } from '../domain/types';
import { createStoredConfiguration } from '../integration/configuration-contract';
import { products as localProducts } from '../products';
import { commands } from '../store/commands';
import { DesignStore } from '../store/design-store';
import { DesignStoreProvider } from '../store/react';
import { createValidationReport, validateDesign } from '../validation';
import { AppHeader, StepSidebar } from './Stepper';
import { ProductStep } from './ProductStep';
import { OptionsStep } from './OptionsStep';
import { ReviewStep } from './ReviewStep';
import { SummaryStep } from './SummaryStep';

const DesignStep = lazy(() =>
  import('./DesignStep').then(module => ({ default: module.DesignStep })),
);

interface Session {
  product: ProductConfig;
  store: DesignStore;
}

export function App({
  productCatalogue,
  productSource = 'local',
}: {
  productCatalogue?: ProductConfig[];
  productSource?: 'backend' | 'local';
}) {
  const { t } = useTranslations();
  const availableProducts = productCatalogue?.length ? productCatalogue : localProducts;
  const [hydrated, setHydrated] = useState(false);
  const [step, setStep] = useState(1);
  const [added, setAdded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submissionReference, setSubmissionReference] = useState<string>();
  const [submissionError, setSubmissionError] = useState<string>();
  const [session, setSession] = useState<Session>(() => createSession(availableProducts[0]!));
  const workspaceRef = useRef<HTMLElement>(null);

  const chooseProduct = (product: ProductConfig) => {
    if (product.id === session.product.id) return;
    setSession(createSession(product));
    setAdded(false);
    setSubmissionReference(undefined);
    setSubmissionError(undefined);
  };

  const productionReport = () =>
    createValidationReport(validateDesign(session.store.getState().design, session.product));

  const goToStep = (target: number) => {
    if (target >= 5 && !productionReport().canExport) {
      setStep(4);
      return;
    }
    setStep(target);
  };

  const submitConfiguration = async () => {
    if (submitting || !productionReport().canExport) return;
    setSubmitting(true);
    setSubmissionError(undefined);
    try {
      const response = await fetch('/api/configurator/submissions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(await prepareCaptchaBody({
          configuration: createStoredConfiguration(
            session.store.getState().design,
            session.product,
          ),
        }, CaptchaAction.CONFIGURATOR_SUBMIT)),
      });
      const payload = (await response.json().catch(() => null)) as {
        reference?: string;
        message?: string;
      } | null;
      if (!response.ok)
        throw new Error(payload?.message || `Server vrátil chybu ${response.status}.`);
      setSubmissionReference(payload?.reference);
      setAdded(true);
    } catch (error) {
      setAdded(false);
      setSubmissionError(
        error instanceof Error ? error.message : t('text4100'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const next = () => {
    if (step === 4 && !productionReport().canExport) return;
    if (step === 5) {
      if (!productionReport().canExport) {
        setStep(4);
        return;
      }
      void submitConfiguration();
      return;
    }
    setStep(current => Math.min(5, current + 1));
  };

  useEffect(() => {
    setHydrated(true);
  }, []);

  useEffect(() => {
    let previousDesign = session.store.getState().design;
    const save = () => {
      const currentDesign = session.store.getState().design;
      if (currentDesign === previousDesign) return;
      previousDesign = currentDesign;
      setAdded(false);
      setSubmissionReference(undefined);
      setSubmissionError(undefined);
      const report = createValidationReport(validateDesign(currentDesign, session.product));
      setStep(current => (current === 5 && !report.canExport ? 4 : current));
      try {
        localStorage.setItem(
          `lucasart-design:${session.product.id}`,
          JSON.stringify(currentDesign),
        );
      } catch {
        /* Storage can be unavailable in embedded hosts. */
      }
    };
    const unsubscribe = session.store.subscribe(save);
    return unsubscribe;
  }, [session]);

  useEffect(() => {
    workspaceRef.current?.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [step]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (step !== 3) return;
      if (isEditingTarget(event.target)) return;
      const store = session.store;
      const commandKey = event.ctrlKey || event.metaKey;
      if (commandKey && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        if (event.shiftKey) {
          store.redo();
        } else {
          store.undo();
        }
      }
      if (commandKey && event.key.toLowerCase() === 'y') {
        event.preventDefault();
        store.redo();
      }
      if (commandKey && event.key.toLowerCase() === 'd') {
        event.preventDefault();
        store.dispatch(commands.duplicateSelected(store.getState()));
      }
      if (event.key === 'Delete' || event.key === 'Backspace') {
        if (store.getState().selectedObjectIds.length) {
          event.preventDefault();
          store.dispatch(commands.deleteSelected(store.getState()));
        }
      }
      if (
        ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key) &&
        store.getState().selectedObjectIds.length
      ) {
        event.preventDefault();
        const amount = event.shiftKey ? 10 : 1;
        store.dispatch(
          commands.updateSelected(
            store.getState(),
            object => {
              if (event.key === 'ArrowLeft') object.transform.positionMm.x -= amount;
              if (event.key === 'ArrowRight') object.transform.positionMm.x += amount;
              if (event.key === 'ArrowUp') object.transform.positionMm.y -= amount;
              if (event.key === 'ArrowDown') object.transform.positionMm.y += amount;
            },
            'Posunúť objekt',
          ),
        );
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [session, step]);

  return (
    <DesignStoreProvider store={session.store}>
      <div
        className="configurator-app"
        data-configurator-hydrated={hydrated}
        data-product-source={productSource}
      >
        <AppHeader />
        <div className="app-shell">
          <StepSidebar
            current={step}
            onStep={goToStep}
            onBack={() => setStep(current => Math.max(1, current - 1))}
            onNext={next}
            nextPending={submitting}
          />
          <main ref={workspaceRef} className={`workspace ${step === 3 ? 'workspace-editor' : ''}`}>
            {step === 1 && (
              <ProductStep
                products={availableProducts}
                selected={session.product}
                onSelect={chooseProduct}
              />
            )}
            {step === 2 && <OptionsStep product={session.product} store={session.store} />}
            {step === 3 && (
              <Suspense
                fallback={
                  <div className="editor-loading">
                    <span className="spinner" />
                    <strong>Načítavam editor…</strong>
                  </div>
                }
              >
                <DesignStep product={session.product} store={session.store} />
              </Suspense>
            )}
            {step === 4 && (
              <ReviewStep
                product={session.product}
                store={session.store}
                onEditIssue={(viewId, objectId) => {
                  session.store.setActiveView(viewId);
                  session.store.select(objectId ? [objectId] : []);
                  setStep(3);
                }}
              />
            )}
            {step === 5 && (
              <SummaryStep
                product={session.product}
                added={added}
                submissionReference={submissionReference}
                submissionError={submissionError}
              />
            )}
          </main>
        </div>
      </div>
    </DesignStoreProvider>
  );
}

function createSession(product: ProductConfig): Session {
  return { product, store: new DesignStore(createInitialEditorState(product)) };
}

function isEditingTarget(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLElement &&
    (target.matches('input, textarea, select') || target.isContentEditable)
  );
}
